"use client";

/**
 * usePortfolioSocket - Real-time portfolio updates via WebSocket
 *
 * Reuses the shared authenticated socket from usePriceStream (already connected).
 * Listens for backend events after trades (buy/sell) and triggers UI refreshes.
 *
 * Events:
 * - position:bought  → refetch positions
 * - position:sold    → refetch positions (remove/reduce)
 * - balance:update   → update balance display
 * - activity:new     → refetch activity feed
 * - portfolio:refresh → refetch portfolio stats
 * - trade:copied     → copytrade executed, refetch positions
 */

import { useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { notifyPortfolioRefresh } from "@/lib/portfolio-refresh";
import {
  acquireSharedSocket,
  getOrCreateSharedSocket,
  getSharedPriceSocket,
  releaseSharedSocket,
} from "./usePriceStream";

export interface PositionBoughtEvent {
  tokenId: string;
  marketSlug?: string;
  outcome?: string;
  side: string;
  amount: number;
  price: number;
  orderId?: string;
}

export interface PositionSoldEvent {
  positionId: string;
  marketSlug?: string;
  outcome?: string;
  sellSize: number;
  sellPrice: number;
  proceeds: number;
  pnl: number;
  remainingSize: number;
  isClosed: boolean;
}

export interface BalanceUpdateEvent {
  source: string;
  balance: number;
  polBalance?: number;
}

export interface ActivityNewEvent {
  type: "buy" | "sell";
  marketSlug?: string;
  marketTitle?: string;
  outcome?: string;
  size?: number;
  price?: number;
  proceeds?: number;
  pnl?: number;
}

export interface PortfolioRefreshEvent {
  reason: string;
}

interface UsePortfolioSocketOptions {
  onPositionBought?: (event: PositionBoughtEvent) => void;
  onPositionSold?: (event: PositionSoldEvent) => void;
  onBalanceUpdate?: (event: BalanceUpdateEvent) => void;
  onActivityNew?: (event: ActivityNewEvent) => void;
  onPortfolioRefresh?: (event: PortfolioRefreshEvent) => void;
  onTradeCopied?: (event: any) => void;
  onTradeEvent?: (type: string, event: any) => void;
  enabled?: boolean;
}

export function usePortfolioSocket(options: UsePortfolioSocketOptions = {}) {
  const { enabled = true } = options;
  const { isLoggedIn } = useAuth();
  const callbacksRef = useRef(options);
  callbacksRef.current = options;

  useEffect(() => {
    if (!enabled || !isLoggedIn) return;
    acquireSharedSocket();

    // Broadcast through the shared `notifyPortfolioRefresh` helper so the
    // backend's tight WS cluster (`position:sold` + `activity:new` +
    // `portfolio:refresh` arrive within ~50-100ms) coalesces into a
    // single window-event burst — and stays coalesced even when a
    // manual trade's response handler fires its own dispatch in the
    // same window. Without that, every consumer ran its 0/1.5s/4s
    // retry schedule once per source event (~9 fetches per consumer
    // per trade).
    const broadcastRefresh = () => {
      notifyPortfolioRefresh();
    };

    const handlers: Record<string, (data: any) => void> = {
      "position:bought": (data) => {
        callbacksRef.current.onPositionBought?.(data);
        callbacksRef.current.onTradeEvent?.("position:bought", data);
        broadcastRefresh();
      },
      "position:sold": (data) => {
        callbacksRef.current.onPositionSold?.(data);
        callbacksRef.current.onTradeEvent?.("position:sold", data);
        broadcastRefresh();
      },
      "balance:update": (data) => {
        callbacksRef.current.onBalanceUpdate?.(data);
        callbacksRef.current.onTradeEvent?.("balance:update", data);
      },
      "activity:new": (data) => {
        callbacksRef.current.onActivityNew?.(data);
        callbacksRef.current.onTradeEvent?.("activity:new", data);
        broadcastRefresh();
      },
      "portfolio:refresh": (data) => {
        callbacksRef.current.onPortfolioRefresh?.(data);
        callbacksRef.current.onTradeEvent?.("portfolio:refresh", data);
        broadcastRefresh();
      },
      "trade:copied": (data) => {
        callbacksRef.current.onTradeCopied?.(data);
        callbacksRef.current.onTradeEvent?.("trade:copied", data);
        broadcastRefresh();
      },
      // Server pushes 'dflow:redeemed' from dflow-position-monitor when an
      // auto-redeem succeeds. Without this handler the redeemed position
      // lingered in MyPositions / Claim badge / TG bot until manual refresh.
      "dflow:redeemed": (data) => {
        callbacksRef.current.onTradeEvent?.("dflow:redeemed", data);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("dflow:refresh"));
          notifyPortfolioRefresh();
        }
      },
    };

    // Attach to shared price stream socket.
    const attach = (socket?: ReturnType<typeof getSharedPriceSocket>) => {
      const currentSocket = socket || getSharedPriceSocket();
      if (!currentSocket) return false;
      for (const [event, handler] of Object.entries(handlers)) {
        currentSocket.off(event, handler);
        currentSocket.on(event, handler);
      }
      return true;
    };

    const detach = (socket?: ReturnType<typeof getSharedPriceSocket>) => {
      const currentSocket = socket || getSharedPriceSocket();
      if (!currentSocket) return;
      for (const [event, handler] of Object.entries(handlers)) {
        currentSocket.off(event, handler);
      }
    };

    let cancelled = false;
    let attachedSocket: ReturnType<typeof getSharedPriceSocket> = null;

    const ensureSocket = async () => {
      const socket = getSharedPriceSocket() || await getOrCreateSharedSocket();
      if (cancelled || !socket) return false;
      attachedSocket = socket;
      return attach(socket);
    };

    // Try to attach immediately if a socket already exists.
    const attached = attach();
    if (attached) {
      attachedSocket = getSharedPriceSocket();
    }

    // If socket not ready yet, create or wait for the shared socket.
    let pollTimer: NodeJS.Timeout | null = null;
    if (!attached) {
      ensureSocket();
      let attempts = 0;
      pollTimer = setInterval(() => {
        attempts++;
        if (attach(attachedSocket || undefined) || attempts > 20) {
          if (pollTimer) clearInterval(pollTimer);
          pollTimer = null;
        }
      }, 500);
    }

    return () => {
      cancelled = true;
      detach(attachedSocket || undefined);
      releaseSharedSocket();
      if (pollTimer) clearInterval(pollTimer);
    };
  }, [enabled, isLoggedIn]);
}
